import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Schedule as ScheduleIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { format, parse } from 'date-fns';
import { SalonSettings, DEFAULT_SALON_SETTINGS } from '../../types';
import { salonSettingsApi } from '../../firebase/services';

const DAY_NAMES = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

const timeStrToDate = (timeStr: string): Date => {
  return parse(timeStr, 'HH:mm', new Date());
};

const dateToTimeStr = (date: Date | null): string => {
  if (!date) return '09:00';
  return format(date, 'HH:mm');
};

interface SalonScheduleManagementProps {
  settings: SalonSettings;
  onRefresh: () => void;
}

const SalonScheduleManagement: React.FC<SalonScheduleManagementProps> = ({ settings, onRefresh }) => {
  const [formSettings, setFormSettings] = useState<SalonSettings>(() => ({
    businessHours: { ...DEFAULT_SALON_SETTINGS.businessHours, ...settings.businessHours },
    holidays: settings.holidays || [],
  }));
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newHolidayDate, setNewHolidayDate] = useState<Date | null>(null);

  const handleDayToggle = (day: number, isOpen: boolean) => {
    setFormSettings(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: { ...prev.businessHours[day], isOpen },
      },
    }));
  };

  const handleTimeChange = (day: number, field: 'openTime' | 'closeTime', date: Date | null) => {
    setFormSettings(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: { ...prev.businessHours[day], [field]: dateToTimeStr(date) },
      },
    }));
  };

  const handleAddHoliday = () => {
    if (!newHolidayDate) return;
    const dateStr = format(newHolidayDate, 'yyyy-MM-dd');
    if (formSettings.holidays.includes(dateStr)) return;
    setFormSettings(prev => ({
      ...prev,
      holidays: [...prev.holidays, dateStr].sort(),
    }));
    setNewHolidayDate(null);
  };

  const handleRemoveHoliday = (dateStr: string) => {
    setFormSettings(prev => ({
      ...prev,
      holidays: prev.holidays.filter(d => d !== dateStr),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await salonSettingsApi.save(formSettings);
      onRefresh();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
            <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Horaires & Jours fériés
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              Enregistrer
            </Button>
            {saving && (
              <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-10px', ml: '-10px' }} />
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>Horaires enregistrés avec succès !</Alert>}

        {/* Weekly schedule */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Horaires hebdomadaires</Typography>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2}>
            {[1, 2, 3, 4, 5, 6, 0].map((day) => {
              const daySettings = formSettings.businessHours[day] || DEFAULT_SALON_SETTINGS.businessHours[day];
              return (
                <Box
                  key={day}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: daySettings.isOpen ? 'action.hover' : 'grey.50',
                  }}
                >
                  <Box sx={{ width: 120, flexShrink: 0 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={daySettings.isOpen}
                          onChange={(e) => handleDayToggle(day, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography fontWeight={daySettings.isOpen ? 600 : 400} color={daySettings.isOpen ? 'text.primary' : 'text.disabled'}>
                          {DAY_NAMES[day]}
                        </Typography>
                      }
                    />
                  </Box>

                  {daySettings.isOpen ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TimePicker
                        label="Ouverture"
                        value={timeStrToDate(daySettings.openTime)}
                        onChange={(date) => handleTimeChange(day, 'openTime', date)}
                        slotProps={{ textField: { size: 'small', sx: { width: 130 } } }}
                        ampm={false}
                      />
                      <Typography color="text.secondary">—</Typography>
                      <TimePicker
                        label="Fermeture"
                        value={timeStrToDate(daySettings.closeTime)}
                        onChange={(date) => handleTimeChange(day, 'closeTime', date)}
                        slotProps={{ textField: { size: 'small', sx: { width: 130 } } }}
                        ampm={false}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                      Fermé
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Paper>

        {/* Holidays */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Jours fériés / Fermetures exceptionnelles</Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <DatePicker
              label="Ajouter une date fermée"
              value={newHolidayDate}
              onChange={(date) => setNewHolidayDate(date)}
              slotProps={{ textField: { size: 'small' } }}
            />
            <Button
              variant="outlined"
              onClick={handleAddHoliday}
              disabled={!newHolidayDate}
            >
              Ajouter
            </Button>
          </Box>

          {formSettings.holidays.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Aucun jour férié configuré
            </Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {formSettings.holidays.map((dateStr) => (
                <Chip
                  key={dateStr}
                  label={format(new Date(dateStr + 'T12:00:00'), 'dd/MM/yyyy')}
                  onDelete={() => handleRemoveHoliday(dateStr)}
                  deleteIcon={<DeleteIcon />}
                  color="error"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default SalonScheduleManagement;
